import { useEffect, useCallback, useState } from 'react';
import { useParams, useNavigate, generatePath } from 'react-router-dom';
import {
  ValidatorsProps,
  ResponseError,
  responseErrorsResolver,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { changePanelState } from '../../store/slices/primary-accounts';
import {
  useLazyGetPrimaryAccountQuery,
  useUpdatePrimaryAccountMutation,
} from '../../store/services/primary-accounts';
import useChartForm from '../../hooks/use-form';

import ROUTES from '../../../../routes';

const useEdit = () => {
  const {
    accounts: {
      typeOptions,
      panelIsOpen,
      primaryValidation,
      selectedPrimaryAccount,
    },
    categories: { categories },
    groups: { groups },
  } = useAppSelector((state) => state.charts);
  const { activePermissions } = useAppSelector((state) => state.root);
  const [globalError, setGlobalError] = useState('');

  const { primaryAccountId, categoryId, groupId } = useParams();

  const [getPrimaryAccount, { isLoading, isFetching }] =
    useLazyGetPrimaryAccountQuery();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { control, reset, errors, description, handleSubmit } = useChartForm({
    groups,
    categories,
    panelIsOpen,
    schema: primaryValidation as ValidatorsProps,
  });

  const [update, { isLoading: requestInProgress, isSuccess: onChangeSuccess }] =
    useUpdatePrimaryAccountMutation();

  const handleClose = useCallback(() => {
    setGlobalError('');
    dispatch(changePanelState(false));

    setTimeout(() => {
      navigate(
        generatePath(ROUTES.PRIMARY_ACCOUNTS, {
          categoryId: categoryId as string,
          groupId: groupId as string,
          primaryAccountId: primaryAccountId as string,
        }),
      );
    }, 100);
  }, [dispatch]);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const response = (await update({
      body: {
        description: values.description as string,
        groupId: groupId as string,
        active: values.active,
      },
      id: primaryAccountId as string,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError,
      });
    }
  });

  useEffect(() => {
    if (selectedPrimaryAccount) {
      reset({
        description: selectedPrimaryAccount.description,
        active: selectedPrimaryAccount.active,
        groupId,
      });
    }
  }, [selectedPrimaryAccount]);

  useEffect(() => {
    if (primaryAccountId) {
      getPrimaryAccount(primaryAccountId);
    }
  }, [primaryAccountId, dispatch]);

  useEffect(() => {
    dispatch(changePanelState(true));

    return () => {
      dispatch(changePanelState(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (onChangeSuccess) {
      handleClose();
    }
  }, [onChangeSuccess, handleClose]);

  return {
    typeOptions,
    activePermissions,
    globalError,
    primaryAccountId,
    categoryId,
    groupId,
    isLoading,
    isFetching,
    control,
    errors,
    description,
    requestInProgress,
    onSubmit,
    handleClose,
    panelIsOpen,
    categories,
    groups,
    selectedPrimaryAccount,
  };
};

export default useEdit;
